/**
 * Pre-Consultation Chat Component
 * Interactive chat interface for medical pre-visit screening in Portuguese
 */

import React, { useState, useEffect, useRef } from 'react';
import './PreConsultationChat.css';

const PreConsultationChat = ({ onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Initialize chat with welcome message
  useEffect(() => {
    const initMessage = {
      id: 1,
      type: 'assistant',
      content: `Olá! Eu sou o MedGemma, seu assistente médico virtual. 🏥
      
Vou fazer algumas perguntas para entender melhor seus sintomas e preparar sua consulta médica.

Por favor, me conte: qual é o principal motivo da sua consulta hoje?`,
      timestamp: new Date().toISOString()
    };
    setMessages([initMessage]);
    setSessionId(Date.now().toString());
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatMessage = (content) => {
    // Convert line breaks to paragraphs for better formatting
    return content.split('\n').map((line, idx) => 
      line.trim() ? <p key={idx}>{line}</p> : <br key={idx} />
    );
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Create assistant message placeholder for streaming
    const assistantMessageId = messages.length + 2;
    const assistantMessage = {
      id: assistantMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };
    
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Call MedGemma API with streaming
      const response = await fetch('https://medgemma-vllm.semdor.life/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'google/medgemma-4b-it',
          messages: [
            {
              role: 'system',
              content: `Você é o Dr. MedGemma, um assistente médico virtual especializado em realizar pré-consultas em português brasileiro.
              
Sua abordagem:
              - Seja empático, acolhedor e profissional
              - Converse de forma natural, como um médico real faria
              - Faça perguntas relevantes baseadas nas respostas do paciente
              - Explore sintomas, duração, intensidade e histórico quando apropriado
              - Ofereça orientações preliminares quando tiver informações suficientes
              - SEMPRE recomende consulta médica presencial quando necessário
              - Nunca faça diagnósticos definitivos, apenas orientações preliminares`
            },
            ...messages.filter(msg => !msg.isStreaming).map(msg => ({
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content
            })),
            {
              role: 'user',
              content: userMessage.content
            }
          ],
          temperature: 0.7,
          max_tokens: 500,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.choices && parsed.choices[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                fullContent += content;
                
                // Update message with streaming content
                setMessages(prev => prev.map(msg => 
                  msg.id === assistantMessageId 
                    ? { ...msg, content: fullContent, isStreaming: true }
                    : msg
                ));
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }

      // Mark streaming as complete
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId 
          ? { ...msg, content: fullContent, isStreaming: false }
          : msg
      ));

    } catch (error) {
      console.error('Error calling MedGemma:', error);
      
      // Update the assistant message with error
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId
          ? {
              ...msg,
              content: `Desculpe, houve um problema ao processar sua mensagem. Por favor, tente novamente.
              
${error.message === 'API error: 404' ? 'O servidor MedGemma pode estar offline ou a URL está incorreta.' : 'Erro: ' + error.message}`,
              isError: true,
              isStreaming: false
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const generateReport = () => {
    let report = `RELATÓRIO DE PRÉ-CONSULTA
    
Data: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}
Sessão: ${sessionId}

${'='.repeat(50)}
CONVERSA COMPLETA:
${'='.repeat(50)}\n\n`;
    
    messages.forEach((msg) => {
      if (!msg.isStreaming && msg.content) {
        const sender = msg.type === 'user' ? 'PACIENTE' : 'DR. MEDGEMMA';
        const time = new Date(msg.timestamp).toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
        report += `[${time}] ${sender}:\n${msg.content}\n\n`;
      }
    });

    report += `\n${'='.repeat(50)}\nFIM DO RELATÓRIO\n${'='.repeat(50)}`;

    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pre-consulta-${sessionId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const restartChat = () => {
    setMessages([{
      id: 1,
      type: 'assistant',
      content: `Olá! Eu sou o MedGemma, seu assistente médico virtual. 🏥
      
Vou fazer algumas perguntas para entender melhor seus sintomas e preparar sua consulta médica.

Por favor, me conte: qual é o principal motivo da sua consulta hoje?`,
      timestamp: new Date().toISOString()
    }]);
    setConversationEnded(false);
    setSessionId(Date.now().toString());
  };

  return (
    <div className="pre-consultation-chat">
      <div className="chat-header">
        <button className="back-button" onClick={onBack}>
          ← Voltar
        </button>
        <div className="header-content">
          <img src="/assets/medgemma.avif" alt="MedGemma" className="header-logo" />
          <div className="header-text">
            <h2>Pré-Consulta Interativa</h2>
            <p>Assistente médico virtual em português</p>
          </div>
        </div>
      </div>

      <div className="chat-container" ref={chatContainerRef}>
        <div className="messages-list">
          {messages.map(message => (
            <div 
              key={message.id} 
              className={`message ${message.type} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-avatar">
                {message.type === 'user' ? (
                  <div className="user-avatar">👤</div>
                ) : (
                  <img src="/assets/ai_headshot.svg" alt="MedGemma" className="ai-avatar" />
                )}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-sender">
                    {message.type === 'user' ? 'Você' : 'MedGemma'}
                  </span>
                  <span className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString('pt-BR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <div className={`message-text ${message.isStreaming ? 'streaming' : ''}`}>
                  {formatMessage(message.content)}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message assistant loading">
              <div className="message-avatar">
                <img src="/assets/ai_headshot.svg" alt="MedGemma" className="ai-avatar" />
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-input-container">
        <div className="input-actions">
          {messages.length > 2 && (
            <button onClick={generateReport} className="mini-action-button" title="Baixar relatório da conversa">
              📄
            </button>
          )}
          {messages.length > 1 && (
            <button onClick={restartChat} className="mini-action-button" title="Reiniciar conversa">
              🔄
            </button>
          )}
        </div>
        <div className="input-wrapper">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem aqui..."
            disabled={isLoading}
            rows="2"
            className="chat-input"
          />
          <button 
            onClick={handleSendMessage} 
            disabled={!inputValue.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? '⏳' : '➤'}
          </button>
        </div>
      </div>

      <div className="chat-disclaimer">
        <p>
          ⚠️ Este é um assistente de IA para fins educacionais. 
          Sempre consulte um profissional de saúde para diagnóstico e tratamento médico.
        </p>
      </div>
    </div>
  );
};

export default PreConsultationChat;
